import React, { useMemo, useState } from 'react';

const CURRENT_YEAR = new Date().getFullYear();
const resolveInitialLocale = () => {
    if (typeof navigator === 'undefined') {
        return 'en';
    }
    return navigator.language && navigator.language.toLowerCase().startsWith('id') ? 'id' : 'en';
};

const COPY = {
    id: {
        appName: 'Hening App',
        title: 'Support',
        intro: 'Halaman ini disediakan sebagai support page resmi untuk pengguna aplikasi Hening. Jika Anda mengalami kendala akun, bug, pembelian, atau pertanyaan umum, hubungi tim kami melalui kontak di bawah.',
        emailSupport: 'Email Support',
        backHome: 'Kembali ke Beranda',
        contactTitle: 'Kontak',
        responseTime: 'Estimasi waktu respon: 1-3 hari kerja',
        supportLanguage: 'Bahasa dukungan: Indonesia dan English',
        beforeContactTitle: 'Sebelum Menghubungi Support',
        steps: [
            'Pastikan aplikasi sudah pada versi terbaru.',
            'Coba tutup dan buka ulang aplikasi.',
            'Restart perangkat jika aplikasi tidak merespons.',
            'Jika masalah tetap muncul, kirim detail perangkat, versi OS, dan screenshot error.'
        ],
        faqTitle: 'FAQ',
        faqOpenQuestion: 'Bagaimana jika aplikasi tidak bisa dibuka?',
        faqOpenAnswer: 'Coba update aplikasi ke versi terbaru, restart perangkat, lalu coba lagi.',
        faqBugQuestion: 'Bagaimana cara melaporkan bug?',
        faqBugAnswer: 'Kirim email ke support dengan langkah reproduksi, screenshot, dan model perangkat.',
        faqLegalQuestion: 'Bagaimana kebijakan privasi dan syarat layanan?',
        faqLegalAnswerPrefix: 'Lihat halaman',
        andWord: 'dan',
        privacyLabel: 'Privacy Policy',
        termsLabel: 'Terms & Conditions',
        pageLanguage: 'Bahasa halaman'
    },
    en: {
        appName: 'Hening App',
        title: 'Support',
        intro: 'This page is the official support page for Hening app users. If you have account, bug, purchase, or general questions, please contact our team using the details below.',
        emailSupport: 'Email Support',
        backHome: 'Back to Home',
        contactTitle: 'Contact',
        responseTime: 'Estimated response time: 1-3 business days',
        supportLanguage: 'Support languages: Indonesian and English',
        beforeContactTitle: 'Before Contacting Support',
        steps: [
            'Make sure the app is updated to the latest version.',
            'Close and reopen the app.',
            'Restart your device if the app is not responding.',
            'If the issue persists, send your device info, OS version, and an error screenshot.'
        ],
        faqTitle: 'FAQ',
        faqOpenQuestion: 'What if the app cannot be opened?',
        faqOpenAnswer: 'Update to the latest version, restart your device, then try again.',
        faqBugQuestion: 'How do I report a bug?',
        faqBugAnswer: 'Send support an email with reproduction steps, screenshots, and device model.',
        faqLegalQuestion: 'Where can I read the legal pages?',
        faqLegalAnswerPrefix: 'See',
        andWord: 'and',
        privacyLabel: 'Privacy Policy',
        termsLabel: 'Terms & Conditions',
        pageLanguage: 'Page language'
    }
};

const Support = () => {
    const [locale, setLocale] = useState(resolveInitialLocale);
    const activeLocale = useMemo(() => (locale === 'id' ? 'id' : 'en'), [locale]);
    const text = COPY[activeLocale];

    return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-indigo-100 py-10 px-4">
            <div className="mx-auto max-w-4xl">
                <section className="rounded-2xl border border-indigo-100 bg-white p-6 md:p-8 shadow-lg">
                    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-indigo-100 pb-4">
                        <p className="text-sm font-semibold text-indigo-600">{text.pageLanguage}</p>
                        <div className="inline-flex rounded-full border border-indigo-200 p-1">
                            <button
                                type="button"
                                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                                    activeLocale === 'id' ? 'bg-indigo-700 text-white' : 'text-indigo-700 hover:bg-indigo-50'
                                }`}
                                onClick={() => setLocale('id')}
                            >
                                Indonesia
                            </button>
                            <button
                                type="button"
                                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                                    activeLocale === 'en' ? 'bg-indigo-700 text-white' : 'text-indigo-700 hover:bg-indigo-50'
                                }`}
                                onClick={() => setLocale('en')}
                            >
                                English
                            </button>
                        </div>
                    </div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-indigo-500">{text.appName}</p>
                    <h1 className="mt-2 text-3xl md:text-4xl font-black text-indigo-900">{text.title}</h1>
                    <p className="mt-4 text-slate-700 leading-relaxed">
                        {text.intro}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <a
                            href="mailto:dimas.nath@icloud.com?subject=Hening%20Support%20Request"
                            className="inline-flex items-center justify-center rounded-full bg-indigo-700 px-5 py-2 text-sm font-semibold text-white transition hover:bg-indigo-800"
                        >
                            {text.emailSupport}
                        </a>
                        <a
                            href="/"
                            className="inline-flex items-center justify-center rounded-full border border-indigo-200 bg-white px-5 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50"
                        >
                            {text.backHome}
                        </a>
                    </div>
                </section>

                <section className="mt-5 rounded-2xl border border-indigo-100 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-bold text-indigo-900">{text.contactTitle}</h2>
                    <ul className="mt-3 list-disc pl-5 text-slate-700 leading-relaxed space-y-1">
                        <li>Email: <a className="text-indigo-700 underline" href="mailto:dimas.nath@icloud.com">dimas.nath@icloud.com</a></li>
                        <li>{text.responseTime}</li>
                        <li>{text.supportLanguage}</li>
                    </ul>
                </section>

                <section className="mt-5 rounded-2xl border border-indigo-100 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-bold text-indigo-900">{text.beforeContactTitle}</h2>
                    <ol className="mt-3 list-decimal pl-5 text-slate-700 leading-relaxed space-y-1">
                        {text.steps.map((step) => (
                            <li key={step}>{step}</li>
                        ))}
                    </ol>
                </section>

                <section className="mt-5 rounded-2xl border border-indigo-100 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-bold text-indigo-900">{text.faqTitle}</h2>
                    <div className="mt-3 space-y-4 text-slate-700 leading-relaxed">
                        <div>
                            <h3 className="font-semibold text-slate-900">{text.faqOpenQuestion}</h3>
                            <p>{text.faqOpenAnswer}</p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">{text.faqBugQuestion}</h3>
                            <p>{text.faqBugAnswer}</p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">{text.faqLegalQuestion}</h3>
                            <p>
                                {text.faqLegalAnswerPrefix}{' '}
                                <a className="text-indigo-700 underline" href="/privacy-policy">{text.privacyLabel}</a> {text.andWord}{' '}
                                <a className="text-indigo-700 underline" href="/terms-and-conditions">{text.termsLabel}</a>.
                            </p>
                        </div>
                    </div>
                </section>

                <p className="mt-6 text-center text-sm text-indigo-500">&copy; Hening App {CURRENT_YEAR}</p>
            </div>
        </div>
    );
};

export default Support;
